import React, { useEffect, useState } from "react";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Button, Chip, Menu, MenuItem, Popover, Stack, TextField, Typography } from "@mui/material";
import { PipelineDimensionOption } from "store/types";

import { PIPELINE_MAX_GROUP_DEPTH } from "../../../constants/deal";

interface Props {
  /** Available group-by dimensions: `key` is sent to the API, `label` is shown. */
  dimensions: PipelineDimensionOption[];
  /** Currently applied dimension keys (lowercase), ordered by nesting. */
  groupBy: string[];
  onChange: (groupBy: string[]) => void;
  /** Number of group levels. 1 → single dropdown (applies instantly); >1 → one popover with N selects + Apply. */
  maxDepth?: number;
}

const chipSx = {
  height: "24px",
  fontSize: "13px",
  color: "#0a3142",
  bgcolor: "#0a31420f",
  borderRadius: "12px",
  cursor: "pointer",
  "& .MuiChip-label": { px: "10px" }
} as const;

const btnSx = { textTransform: "none", borderRadius: "20px", fontSize: "13px", minWidth: "72px" } as const;

const label = "Group by:";

export const GroupBySelector: React.FC<Props> = ({
  dimensions,
  groupBy,
  onChange,
  maxDepth = PIPELINE_MAX_GROUP_DEPTH
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // Draft edited inside the popover (multi-level); committed only on Apply so multi-level picks are one request.
  const [draft, setDraft] = useState<string[]>(groupBy);

  useEffect(() => setDraft(groupBy), [groupBy]);

  const labelFor = (key: string | undefined) => (key && dimensions.find((d) => d.key === key)?.label) || "None";

  const open = (event: React.MouseEvent<HTMLElement>) => {
    setDraft(groupBy);
    setAnchorEl(event.currentTarget);
  };
  const close = () => setAnchorEl(null);

  // ----- Single level: dropdown menu, applies immediately -----
  if (maxDepth <= 1) {
    const selectSingle = (key: string[]) => {
      onChange(key);
      close();
    };
    return (
      <Stack direction="row" alignItems="center" gap="6px">
        <Typography sx={{ fontSize: "13px", fontWeight: "bold", color: "#0a3142", whiteSpace: "nowrap" }}>
          {label}
        </Typography>
        <Chip size="small" label={labelFor(groupBy[0])} onClick={open} sx={chipSx} />
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
          <MenuItem selected={!groupBy.length} onClick={() => selectSingle([])}>
            None
          </MenuItem>
          {dimensions.map((dimension) => (
            <MenuItem
              key={dimension.key}
              selected={groupBy[0] === dimension.key}
              onClick={() => selectSingle([dimension.key])}
            >
              {dimension.label}
            </MenuItem>
          ))}
        </Menu>
      </Stack>
    );
  }

  // ----- Multiple levels: one popover with N selects + Apply/Cancel -----
  const setLevel = (level: number, value: string) => {
    setDraft((prev) => {
      if (!value) return prev.slice(0, level); // "None" drops this level and every deeper one
      const next = prev.slice(0, level);
      next[level] = value;
      return next;
    });
  };

  const apply = () => {
    onChange(draft);
    close();
  };

  const levels = Array.from({ length: maxDepth }, (_, i) => i);
  const optionsForLevel = (level: number) => dimensions.filter((d) => !draft.slice(0, level).includes(d.key));

  return (
    <Stack direction="row" alignItems="center" gap="6px">
      <Typography sx={{ fontSize: "13px", fontWeight: "bold", color: "#0a3142", whiteSpace: "nowrap" }}>
        {label}
      </Typography>
      {levels.map((level, index) => (
        <React.Fragment key={level}>
          {index > 0 && <ChevronRightIcon sx={{ fontSize: "16px", color: "#bac7ce" }} />}
          <Chip size="small" label={labelFor(groupBy[level])} onClick={open} sx={chipSx} />
        </React.Fragment>
      ))}

      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { p: "16px", width: 260, borderRadius: "10px", mt: "6px" } } }}
      >
        <Stack gap="12px">
          {levels.map((level) => {
            const disabled = level > 0 && !draft[level - 1];
            return (
              <TextField
                key={level}
                select
                size="small"
                label={`Group ${level + 1}`}
                value={draft[level] ?? ""}
                disabled={disabled}
                onChange={(e) => setLevel(level, e.target.value)}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { style: { maxHeight: 280 } } } }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              >
                <MenuItem value="">None</MenuItem>
                {optionsForLevel(level).map((dimension) => (
                  <MenuItem key={dimension.key} value={dimension.key}>
                    {dimension.label}
                  </MenuItem>
                ))}
              </TextField>
            );
          })}

          <Stack direction="row" justifyContent="flex-end" gap="8px" sx={{ mt: "4px" }}>
            <Button variant="outlined" onClick={close} sx={btnSx}>
              Cancel
            </Button>
            <Button variant="contained" onClick={apply} sx={btnSx}>
              Apply
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </Stack>
  );
};
